import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

import { useBulkImportApi } from '@/services/bulkImport'
import { bulkImportExportFileNames } from '@/services/bulkImport/constants'
import { pollBulkImportUntil, isBulkImportPollError } from '@/composables/bulkImport/bulkImportPoll'
import { ApiErrorException } from '@/types/apiErrors'
import i18n from '@/plugins/i18n'
import { isPartialCompletion } from '@/composables/bulkImport/useBulkImportStatus'
import { useActiveBulkImportsStore } from '@/stores/activeBulkImports'
import { saveBlobAsFile } from '@/utils/downloadAuthenticatedFile'
import type { AuthenticatedBlobResult } from '@/utils/downloadAuthenticatedFile'
import type { BulkImport, BulkImportEntity, BulkImportErrorRow, BulkImportMode, BulkImportStep } from '@/types/bulkImport'
import type { PagyMeta } from '@/api/responseExtractors'

export type BulkExportToastStatus = 'idle' | 'preparing' | 'finalized' | 'downloaded' | 'error'

const EXPORT_PROGRESS_TICK_MS = 200
const EXPORT_PROGRESS_CEILING = 90
const EXPORT_ESTIMATED_SECONDS = 10
const EXPORT_FINALIZE_DELAY_MS = 400

export const useBulkImportStore = defineStore('corp-bulk-import', () => {
  const api = useBulkImportApi()

  // states
  const entity = ref<BulkImportEntity>('users')
  const mode = ref<BulkImportMode>('create')
  const step = ref<BulkImportStep>('selector')
  const file = ref<File | null>(null)
  const currentImport = ref<BulkImport | null>(null)
  const errors = ref<BulkImportErrorRow[]>([])
  const errorsPagination = ref<PagyMeta | null>(null)
  const ignoredRows = ref<BulkImportErrorRow[]>([])
  const ignoredPagination = ref<PagyMeta | null>(null)
  const error = ref<string | null>(null)
  const uploadErrorReason = ref<'row_limit' | null>(null)
  const drawerOpen = ref(false)

  function setDrawerOpen(value: boolean) {
    drawerOpen.value = value
  }

  const isUploading = ref(false)
  const isValidating = ref(false)
  const isPreviewing = ref(false)
  const isConfirming = ref(false)
  const isLoadingErrors = ref(false)
  const isExportingSelection = ref(false)

  // getters
  const importId = computed(() => currentImport.value?.id ?? null)
  const totals = computed(() => currentImport.value?.totals ?? null)

  let pollAbort: AbortController | null = null
  let stopMirror: (() => void) | null = null

  // private
  function clearState() {
    pollAbort?.abort()
    pollAbort = null
    stopMirror?.()
    stopMirror = null
    file.value = null
    currentImport.value = null
    errors.value = []
    errorsPagination.value = null
    ignoredRows.value = []
    ignoredPagination.value = null
    error.value = null
    uploadErrorReason.value = null
    isUploading.value = false
    isValidating.value = false
    isPreviewing.value = false
    isConfirming.value = false
    isLoadingErrors.value = false
  }

  function extractMessage(err: unknown): string {
    if (err instanceof Error) return err.message
    return String(err)
  }

  // Excesso de linhas é validado no back (create). O contrato usa errors[].code
  // 'too_many_rows'; o match por mensagem é fallback temporário até o back publicar.
  function isRowLimitError(err: unknown): boolean {
    if (err instanceof ApiErrorException && err.getPrimaryErrorCode() === 'too_many_rows') {
      return true
    }
    return extractMessage(err).includes('mais linhas do que o permitido')
  }

  // Timeout do axios (ECONNABORTED) ou queda de rede: sem resposta HTTP.
  function isConnectionInterrupted(err: unknown): boolean {
    if (err instanceof ApiErrorException) return err.getStatus() === 0
    return err instanceof Error && 'code' in err && (err as { code?: string }).code === 'ECONNABORTED'
  }

  function stepForCompletedImport(failedCount: number, hasCardFailures: boolean): BulkImportStep {
    return isPartialCompletion(failedCount, hasCardFailures) ? 'result_partial' : 'result_success'
  }

  // actions
  function open(newEntity: BulkImportEntity) {
    clearState()
    entity.value = newEntity
    mode.value = 'create'
    step.value = 'selector'
  }

  function startEditFlow(newEntity: BulkImportEntity) {
    clearState()
    entity.value = newEntity
    mode.value = 'edit'
    step.value = 'upload'
  }

  async function openExisting(imp: BulkImport) {
    clearState()
    entity.value = imp.entity
    mode.value = imp.operation_type === 'edit' ? 'edit' : 'create'
    currentImport.value = imp

    switch (imp.status) {
      case 'processing':
      case 'validating':
      case 'uploaded':
      case 'validated':
      case 'ready':
        step.value = 'processing'
        useActiveBulkImportsStore().trackDismissed(imp)
        mirrorFromActive(imp.id)
        break
      case 'completed':
        step.value = stepForCompletedImport(imp.totals.failed, imp.has_card_failures)
        break
      case 'failed':
        step.value = 'result_partial'
        break
      case 'invalid': {
        const loaded = await loadErrorsPage(1)
        step.value = loaded ? 'validation_error' : 'processing'
        break
      }
      default:
        step.value = 'processing'
    }
  }

  async function reopenById(reopenEntity: BulkImportEntity, id: string) {
    try {
      const imp = await api.fetchImportStatus(reopenEntity, id)
      useActiveBulkImportsStore().upsertFromImport(imp)
      await openExisting(imp)
      setDrawerOpen(true)
    } catch (err) {
      error.value = extractMessage(err)
    }
  }

  const manualAddRequest = ref(0)
  function requestManualAdd() {
    manualAddRequest.value++
  }

  function reset() {
    clearState()
    mode.value = 'create'
    step.value = 'selector'
  }

  function chooseBulk() {
    step.value = 'upload'
  }

  function setFile(f: File) {
    file.value = f
    uploadErrorReason.value = null
  }

  function replaceFile() {
    file.value = null
    currentImport.value = null
    error.value = null
    uploadErrorReason.value = null
  }

  async function analyze() {
    if (!file.value) return
    error.value = null
    uploadErrorReason.value = null

    isUploading.value = true
    let uploaded: BulkImport
    try {
      uploaded =
        mode.value === 'edit'
          ? await api.createImport(entity.value, file.value, 'edit')
          : await api.createImport(entity.value, file.value)
      currentImport.value = uploaded
    } catch (err) {
      if (isRowLimitError(err)) {
        uploadErrorReason.value = 'row_limit'
        file.value = null
      } else {
        error.value = isConnectionInterrupted(err)
          ? i18n.global.t('corp.bulk_import.upload.connection_interrupted')
          : extractMessage(err)
      }
      isUploading.value = false
      return
    }
    isUploading.value = false

    const id = uploaded.id

    isValidating.value = true
    try {
      await api.validateImport(entity.value, id)
    } catch (err) {
      error.value = isConnectionInterrupted(err)
        ? i18n.global.t('corp.bulk_import.upload.connection_interrupted')
        : extractMessage(err)
      isValidating.value = false
      return
    }
    step.value = 'validating'

    pollAbort = new AbortController()
    const { signal } = pollAbort
    let terminal: BulkImport
    try {
      terminal = await pollBulkImportUntil(['validated', 'invalid'], {
        fetchStatus: () => api.fetchImportStatus(entity.value, id),
        signal,
      })
      currentImport.value = terminal
    } catch (err) {
      if (signal.aborted) return
      error.value =
        isBulkImportPollError(err) && err.kind === 'timeout'
          ? i18n.global.t('corp.bulk_import.upload.validation_timeout')
          : extractMessage(err)
      isValidating.value = false
      step.value = 'upload'
      return
    }
    isValidating.value = false

    if (terminal.status === 'validated') {
      isPreviewing.value = true
      let previewed: BulkImport
      try {
        previewed = await api.previewImport(entity.value, id)
        currentImport.value = previewed
      } catch (err) {
        error.value = extractMessage(err)
        isPreviewing.value = false
        step.value = 'upload'
        return
      }
      isPreviewing.value = false
      step.value =
        previewed.status === 'completed'
          ? stepForCompletedImport(previewed.totals.failed, previewed.has_card_failures)
          : 'summary'
    } else {
      // Só vai pra validation_error se os erros carregarem, senão mascara falha de rede como planilha errada.
      const loaded = await loadErrorsPage(1)
      step.value = loaded ? 'validation_error' : 'upload'
    }
  }

  async function confirm() {
    const id = importId.value
    if (!id) return
    if ((totals.value?.valid ?? 0) === 0) return
    error.value = null

    isConfirming.value = true
    let confirmed: BulkImport
    try {
      confirmed = await api.confirmImport(entity.value, id)
    } catch (err) {
      error.value = extractMessage(err)
      isConfirming.value = false
      return
    }
    isConfirming.value = false

    if (confirmed.status === 'completed') {
      currentImport.value = confirmed
      step.value = stepForCompletedImport(confirmed.totals.failed, confirmed.has_card_failures)
      return
    }
    if (confirmed.status === 'failed') {
      currentImport.value = confirmed
      step.value = 'result_partial'
      return
    }

    const processingView: BulkImport = {
      ...confirmed,
      progress: {
        processed_count: 0,
        total_count: confirmed.progress?.total_count ?? 0,
        percent: 0,
      },
    }
    currentImport.value = processingView
    step.value = 'processing'

    const active = useActiveBulkImportsStore()
    active.track(processingView)
    mirrorFromActive(id)
  }

  function mirrorFromActive(id: string) {
    const active = useActiveBulkImportsStore()
    stopMirror?.()
    stopMirror = watch(
      () => active.byId(id),
      (a) => {
        if (!a || currentImport.value?.id !== id) return
        currentImport.value = {
          ...currentImport.value,
          status: a.status,
          totals: a.totals,
          progress: {
            processed_count: a.processedCount,
            total_count: a.totalCount,
            percent: a.progressPercent,
          },
          cards_failed: a.cardsFailed,
          has_card_failures: a.hasCardFailures,
          has_result_report: a.hasResultReport,
          has_error_report: a.hasErrorReport,
          error_message: a.errorMessage,
          finished_at: a.finishedAt,
        } as BulkImport
        if (a.status === 'completed') {
          step.value = stepForCompletedImport(a.totals.failed, a.hasCardFailures)
        } else if (a.status === 'failed') {
          step.value = 'result_partial'
        }
      },
    )
  }

  async function loadErrorsPage(page: number): Promise<boolean> {
    const id = importId.value
    if (!id) return false

    isLoadingErrors.value = true
    try {
      const { rows, pagination } = await api.fetchImportErrors(entity.value, id, page)
      errors.value = rows
      errorsPagination.value = pagination
      return true
    } catch (err) {
      error.value = extractMessage(err)
      return false
    } finally {
      isLoadingErrors.value = false
    }
  }

  async function loadIgnoredPage(page = 1): Promise<boolean> {
    const id = importId.value
    if (!id) return false

    try {
      const { rows, pagination } = await api.fetchImportErrors(entity.value, id, page, ['ignored'])
      ignoredRows.value = rows
      ignoredPagination.value = pagination
      return true
    } catch (err) {
      error.value = extractMessage(err)
      return false
    }
  }

  function resetForUpload() {
    pollAbort?.abort()
    pollAbort = null
    stopMirror?.()
    stopMirror = null
    file.value = null
    currentImport.value = null
    errors.value = []
    errorsPagination.value = null
    ignoredRows.value = []
    ignoredPagination.value = null
    error.value = null
    uploadErrorReason.value = null
    step.value = 'upload'
  }

  function retryUpload() {
    resetForUpload()
  }

  function startNewImport() {
    resetForUpload()
  }

  async function downloadTemplate() {
    if (entity.value === 'cards') return
    try {
      await api.downloadImportTemplate(entity.value)
    } catch (err) {
      error.value = extractMessage(err)
    }
  }

  async function downloadReport(kind: 'error' | 'result', format: 'xlsx' | 'csv' = 'xlsx') {
    const id = importId.value
    if (!id) return

    try {
      await api.downloadImportReport(entity.value, id, kind, format)
    } catch (err) {
      error.value = extractMessage(err)
    }
  }
  async function exportSelectedForEdit(
    exportEntity: BulkImportEntity,
    uuids: string[],
  ): Promise<boolean> {
    if (uuids.length === 0) return false
    error.value = null

    isExportingSelection.value = true
    try {
      await api.exportPrefilled(exportEntity, uuids)
      return true
    } catch (err) {
      error.value = extractMessage(err)
      return false
    } finally {
      isExportingSelection.value = false
    }
  }

  // states — toast de export da planilha de edição (progresso simulado)
  const exportToastStatus = ref<BulkExportToastStatus>('idle')
  const exportToastEntity = ref<BulkImportEntity>('users')
  const exportToastProgress = ref(0)
  const exportToastRemainingSeconds = ref(EXPORT_ESTIMATED_SECONDS)

  // private — blob retido para o re-download e timers da simulação
  let exportedBlob: Blob | null = null
  let exportedFilename: string | null = null
  let exportProgressTimer: ReturnType<typeof setInterval> | null = null
  let exportFinalizeTimer: ReturnType<typeof setTimeout> | null = null

  function _clearExportTimers() {
    if (exportProgressTimer) {
      clearInterval(exportProgressTimer)
      exportProgressTimer = null
    }
    if (exportFinalizeTimer) {
      clearTimeout(exportFinalizeTimer)
      exportFinalizeTimer = null
    }
  }

  // Easing simples: aproxima do teto de 90% enquanto a request roda.
  function _tickExportProgress() {
    const current = exportToastProgress.value
    const step = Math.max(1, Math.round((EXPORT_PROGRESS_CEILING - current) * 0.08))
    exportToastProgress.value = Math.min(EXPORT_PROGRESS_CEILING, current + step)
    exportToastRemainingSeconds.value = Math.max(
      1,
      Math.round(EXPORT_ESTIMATED_SECONDS * (1 - exportToastProgress.value / 100)),
    )
  }

  function _beginExportPreparing(exportEntity: BulkImportEntity) {
    _clearExportTimers()
    exportedBlob = null
    exportedFilename = null
    exportToastEntity.value = exportEntity
    exportToastStatus.value = 'preparing'
    exportToastProgress.value = 0
    exportToastRemainingSeconds.value = EXPORT_ESTIMATED_SECONDS
    exportProgressTimer = setInterval(_tickExportProgress, EXPORT_PROGRESS_TICK_MS)
  }

  // Barra cheia visível por um instante antes de trocar para "finalizada".
  function _finishExportSuccess(result: AuthenticatedBlobResult) {
    _clearExportTimers()
    exportToastProgress.value = 100
    exportToastRemainingSeconds.value = 0
    exportedBlob = result.blob
    exportedFilename = result.filename ?? bulkImportExportFileNames[exportToastEntity.value]
    saveBlobAsFile(exportedBlob, exportedFilename)
    exportFinalizeTimer = setTimeout(() => {
      exportToastStatus.value = 'finalized'
      exportFinalizeTimer = null
    }, EXPORT_FINALIZE_DELAY_MS)
  }

  async function startExportFlow(
    exportEntity: BulkImportEntity,
    uuids: string[],
  ): Promise<boolean> {
    if (uuids.length === 0) return false
    const alreadyPreparing = exportToastStatus.value === 'preparing'
    if (alreadyPreparing) return false
    _beginExportPreparing(exportEntity)
    try {
      const result = await api.exportPrefilledBlob(exportEntity, uuids)
      if (exportToastStatus.value !== 'preparing') return false
      _finishExportSuccess(result)
      return true
    } catch {
      _clearExportTimers()
      if (exportToastStatus.value === 'preparing') exportToastStatus.value = 'error'
      return false
    }
  }

  function downloadExportedFile() {
    if (!exportedBlob || !exportedFilename) return
    saveBlobAsFile(exportedBlob, exportedFilename)
    exportToastStatus.value = 'downloaded'
  }

  function dismissExportToast() {
    _clearExportTimers()
    exportToastStatus.value = 'idle'
    exportToastProgress.value = 0
    exportToastRemainingSeconds.value = EXPORT_ESTIMATED_SECONDS
    exportedBlob = null
    exportedFilename = null
  }

  return {
    entity,
    mode,
    step,
    file,
    currentImport,
    errors,
    errorsPagination,
    ignoredRows,
    ignoredPagination,
    error,
    uploadErrorReason,
    isUploading,
    isValidating,
    isPreviewing,
    isConfirming,
    isLoadingErrors,
    isExportingSelection,
    importId,
    totals,
    drawerOpen,
    manualAddRequest,
    open,
    startEditFlow,
    setDrawerOpen,
    openExisting,
    reopenById,
    requestManualAdd,
    reset,
    chooseBulk,
    setFile,
    replaceFile,
    analyze,
    confirm,
    loadErrorsPage,
    loadIgnoredPage,
    retryUpload,
    startNewImport,
    downloadTemplate,
    downloadReport,
    exportSelectedForEdit,
    exportToastStatus,
    exportToastEntity,
    exportToastProgress,
    exportToastRemainingSeconds,
    startExportFlow,
    downloadExportedFile,
    dismissExportToast,
  }
})
